import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import orderBy from 'lodash.orderby';

import { addressesIncludeKey, phoneNumberIncludeKey } from '@/_containers';
import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError } from '@/_shared';
import { apiClient, defaultApiParamStr, queryClient } from '@/_shared';

import type { ContactCreateSchema, ContactQuerySchema, ContactsQueryAllSchema, ContactUpdateSchema } from './schemas';
import { contactQuerySchema } from './schemas';
import type { BirthdaysAllQuerySchema } from './schemas/birthdays-query-schema';

export const useCreateMutation = () =>
  useMutation<{ data: ContactQuerySchema }, ApiError, ContactCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/contacts/',
        payload,
      }),
  });

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['CONTACTS'] });
};

export const useGetContactsMutation: UseControlledMutation<ContactQuerySchema> = (params = {}) =>
  useMutation({
    ...params,
    mutationFn: async ({ search = '', per_page = 25, include = '' }) => {
      return queryClient.fetchQuery({
        queryKey: ['CONTACTS_ASYNC', { search, per_page, include }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<ContactsQueryAllSchema>({
            endpoint: '/api/v1/contacts',
            params: {
              search,
              include: `profile_image${include ? `,${include}` : ''}`,
              per_page,
            },
          });
          return orderBy(res?.data ?? [], ['first_name']) || [];
        },
      });
    },
  });

export function getContactsQuery(apiParamStr = defaultApiParamStr) {
  return {
    queryKey: ['CONTACTS', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<ContactsQueryAllSchema>({
        endpoint: '/api/v1/contacts',
        params: `${apiParamStr}&include=profile_image,primary_client,clients,${phoneNumberIncludeKey()}`,
      });
      return res;
    },
  };
}

export function useGetContacts(apiParamStr: string) {
  return useSuspenseQuery(getContactsQuery(apiParamStr));
}

export function useGetContact(id: string) {
  return useSuspenseQuery(getContactQuery(id));
}

export function getContactQuery(id: string) {
  return {
    queryKey: ['CONTACT', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: ContactQuerySchema }>({
        endpoint: `/api/v1/contacts/${id}?include=marketing_campaigns,emails,alternate_clients.profile_image,primary_client.profile_image,clients.logo,profile_image,friends.profile_image,${phoneNumberIncludeKey()},${addressesIncludeKey()}`,
        responseSchema: contactQuerySchema,
        preventAuditLog: true,
      });
      return res;
    },
  };
}

export const invalidateContactQuery = (id: string) => {
  queryClient.invalidateQueries({ queryKey: ['CONTACT', id] });
};

export function useGetContactAgreements(id: string) {
  return useSuspenseQuery({
    queryKey: ['CONTACT_AGREEMENTS', id],
    queryFn: async () => {
      const res = await apiClient.get<{ agreements: ContactQuerySchema['agreements'] }>({
        endpoint: `/api/v1/contacts/${id}/agreements`,
        responseSchema: contactQuerySchema,
        preventAuditLog: true,
      });
      return res;
    },
  });
}

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: ContactQuerySchema }, ApiError, ContactUpdateSchema>({
    mutationFn: ({ id: bId, ...payload }) =>
      apiClient.request({
        endpoint: `/api/v1/contacts/${id || bId}?include=clients.logo,profile_image,friends.profile_image,${phoneNumberIncludeKey()},${addressesIncludeKey()}`,
        method: 'PUT',
        payload,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['AUDIT_REVISION_HISTORY', 'contacts', id] });
    },
  });

export const usePatchMutation = () =>
  useMutation<{ data: ContactQuerySchema }, ApiError, Partial<ContactUpdateSchema>>({
    mutationFn: ({ id, ...payload }) =>
      apiClient.request({
        endpoint: `/api/v1/contacts/${id}`,
        method: 'PATCH',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['CONTACT_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/contacts/${id}`,
        method: 'DELETE',
      }),
  });

export function useGetContactsUpcomingBirthdays(apiParamStr: string) {
  return useSuspenseQuery(getContactsUpcomingBirthdaysQuery(apiParamStr));
}

export function getContactsUpcomingBirthdaysQuery(apiParamStr = defaultApiParamStr) {
  return {
    queryKey: ['CONTACTS_UPCOMING_BIRTHDAYS', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<BirthdaysAllQuerySchema>({
        endpoint: '/api/v1/upcoming-birthdays',
        params: `${apiParamStr}`,
      });
      return res;
    },
  };
}
